import { org_sector_type_enum } from '@predium/enums';
import { maybeEnum } from '@predium/utils';
import { isNil } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

const STORAGE_KEY = 'status-quo-selection';

type StoredSelection = {
  lastLevel: 'building' | 'portfolio' | 'economic-unit';
  building?: {
    id: number;
    portfolioId: number;
    economicUnitId: number;
  };
  portfolio?: {
    portfolioIds: number[];
  };
  economicUnit?: {
    economicUnitIds: number[];
    portfolioIds?: number[];
  };
};

export function useStatusQuoSelectionStorage() {
  const isBankingOrg = (): boolean => {
    const sector = maybeEnum(org_sector_type_enum, sessionStorage.getItem('org-sector'));
    return sector === org_sector_type_enum.BANKING;
  };

  const readStoredSelection = (): StoredSelection | null => {
    if (!isBankingOrg()) return null;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  };

  const writeStoredSelection = (selection: StoredSelection): void => {
    if (!isBankingOrg()) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
    } catch {}
  };

  const getPortfolioIdsFromSelection = (selection: StoredSelection | null): number[] | null => {
    if (!selection) return null;
    if (selection.lastLevel === 'building' && !isNil(selection.building?.portfolioId)) {
      return [selection.building.portfolioId];
    }
    if (selection.lastLevel === 'economic-unit' && selection.economicUnit?.portfolioIds?.length) {
      return selection.economicUnit.portfolioIds;
    }
    return selection.portfolio?.portfolioIds ?? null;
  };

  const getEconomicUnitIdsFromSelection = (selection: StoredSelection | null): number[] | null => {
    if (!selection) return null;
    if (selection.lastLevel === 'building' && !isNil(selection.building?.economicUnitId)) {
      return [selection.building.economicUnitId];
    }
    if (selection.lastLevel === 'economic-unit' && selection.economicUnit?.economicUnitIds?.length) {
      return selection.economicUnit.economicUnitIds;
    }
    return null;
  };

  const [selection, setSelection] = useState<StoredSelection | null>(() => readStoredSelection());

  const storeBuildingSelection = useCallback((buildingId: number, portfolioId: number, economicUnitId: number) => {
    const next: StoredSelection = {
      lastLevel: 'building',
      building: { id: buildingId, portfolioId, economicUnitId },
    };
    writeStoredSelection(next);
    setSelection(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const storePortfolioSelection = useCallback((portfolioIds: number[]) => {
    const current = readStoredSelection() ?? {};
    const next: StoredSelection = { ...current, lastLevel: 'portfolio', portfolio: { portfolioIds } };
    writeStoredSelection(next);
    setSelection(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const storeEconomicUnitSelection = useCallback((economicUnitIds: number[], portfolioIds?: number[]) => {
    const current = readStoredSelection() ?? {};
    const next: StoredSelection = {
      ...current,
      lastLevel: 'economic-unit',
      economicUnit: { economicUnitIds, portfolioIds },
    };
    writeStoredSelection(next);
    setSelection(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearStoredSelection = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
    setSelection(null);
  }, []);

  const buildingId = selection?.building?.id ?? null;
  const portfolioIds = useMemo(() => getPortfolioIdsFromSelection(selection), [selection]);
  const economicUnitIds = useMemo(() => getEconomicUnitIdsFromSelection(selection), [selection]);

  return {
    buildingId,
    portfolioIds,
    economicUnitIds,
    storeBuildingSelection,
    storePortfolioSelection,
    storeEconomicUnitSelection,
    clearStoredSelection,
  };
}
